import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  Link,
  Typography,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Email, ArrowBack } from "@mui/icons-material";
import { forgotPassword, clearError } from "../../store/slices/authSlice";
import Toast from "../../components/common/Toast";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  const [success, setSuccess] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(forgotPassword(values.email));

      // Success
      if (forgotPassword.fulfilled.match(result)) {

        // Navigate to OTP verification page
        navigate("/verify-otp", {
          state: { email: values.email },
        });

        const msg =
          result?.data?.message ||
          result?.message ||
          "Forgot password successful";

        setSuccess("Forgot password successful");
        setToastMessage(msg);
        setToastSeverity("success");

      }

      // Failure
      if (forgotPassword.rejected.match(result)) {
        const msg =
          result.payload || 
          result.error?.message || 
          result.error?.response?.data?.message || 
          "Failed to send OTP. Please try again later.";
        setToastMessage(msg);
        setToastSeverity("error");
      }
    },
  });

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Watch for error from Redux state and show toast
  useEffect(() => {
    if (error) {
      setToastMessage(error);
      setToastSeverity("error");
    }
  }, [error]);

  const handleCloseToast = () => {
    setToastMessage(null);
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%" }}>
      <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
        Forgot Password
      </Typography>

      <Typography
        variant="body2"
        textAlign="center"
        color="text.secondary"
        mb={3}
      >
        Enter your email address and we'll send you an OTP to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="email"
        name="email"
        label="Email Address *"
        placeholder="Enter your email"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.email && Boolean(formik.errors.email)}
        helperText={formik.touched.email && formik.errors.email}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || !!formik.errors.email}
        sx={{
          py: 1.5,
          mb: 2,
          position: "relative",
          "&:disabled": {
            backgroundColor: "primary.main",
            opacity: 0.7,
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress
              size={20}
              thickness={4}
              sx={{
                color: "white",
                animationDuration: "550ms",
              }}
            />
            <Typography variant="body2" sx={{ color: "white" }}>
              Sending...
            </Typography>
          </Box>
        ) : (
          "Send OTP"
        )}
      </Button>

      <Box sx={{ textAlign: "center" }}>
        <Link
          component={RouterLink}
          to="/login"
          variant="body2"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <ArrowBack fontSize="small" /> Back to Login
        </Link>
      </Box>

      {/* Top-right toast for API response */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default ForgotPassword;


//old code 
// import React, { useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { Link as RouterLink } from "react-router-dom";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import {
//   Box,
//   TextField,
//   Button,
//   Link,
//   Typography,
//   InputAdornment,
//   Alert,
//   CircularProgress,
// } from "@mui/material";
// import { Email, ArrowBack } from "@mui/icons-material";
// import { forgotPassword, clearError } from "../../store/slices/authSlice";

// const validationSchema = Yup.object({
//   email: Yup.string()
//     .email("Invalid email address")
//     .required("Email is required"),
// });

// const ForgotPassword = () => {
//   const dispatch = useDispatch();
//   const { loading, error } = useSelector((state) => state.auth);
//   const [success, setSuccess] = useState(false);

//   const formik = useFormik({
//     initialValues: {
//       email: "",
//     },
//     validationSchema,
//     onSubmit: async (values) => {
//       const result = await dispatch(forgotPassword(values.email));
//       if (!result.error) {
//         setSuccess(true);
//       }
//     },
//   });

//   React.useEffect(() => {
//     return () => {
//       dispatch(clearError());
//     };
//   }, [dispatch]);

//   if (success) {
//     return (
//       <Box sx={{ width: "100%", textAlign: "center" }}>
//         <Typography variant="h5" fontWeight={600} mb={2}>
//           Check Your Email
//         </Typography>
//         <Typography variant="body1" color="text.secondary" mb={3}>
//           We have sent a password reset link to your email address. Please check
//           your inbox and follow the instructions.
//         </Typography>
//         <Button
//           component={RouterLink}
//           to="/login"
//           variant="contained"
//           startIcon={<ArrowBack />}
//         >
//           Back to Login
//         </Button>
//       </Box>
//     );
//   }

//   return (
//     <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%" }}>
//       <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
//         Forgot Password
//       </Typography>
//       <Typography
//         variant="body2"
//         textAlign="center"
//         color="text.secondary"
//         mb={3}
//       >
//         Enter your email address and we'll send you a link to reset your
//         password.
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//       )}

//       <TextField
//         fullWidth
//         id="email"
//         name="email"
//         label="Email Address"
//         placeholder="Enter your email"
//         value={formik.values.email}
//         onChange={formik.handleChange}
//         onBlur={formik.handleBlur}
//         error={formik.touched.email && Boolean(formik.errors.email)}
//         helperText={formik.touched.email && formik.errors.email}
//         InputProps={{
//           startAdornment: (
//             <InputAdornment position="start">
//               <Email color="action" />
//             </InputAdornment>
//           ),
//         }}
//         sx={{ mb: 3 }}
//       />

//       <Button
//         type="submit"
//         fullWidth
//         variant="contained"
//         size="large"
//         disabled={loading}
//         sx={{ py: 1.5, mb: 2 }}
//       >
//         {loading ? (
//           <CircularProgress size={24} color="inherit" />
//         ) : (
//           "Send Reset Link"
//         )}
//       </Button>

//       <Box sx={{ textAlign: "center" }}>
//         <Link
//           component={RouterLink}
//           to="/login"
//           variant="body2"
//           underline="hover"
//           sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
//         >
//           <ArrowBack fontSize="small" /> Back to Login
//         </Link>
//       </Box>
//     </Box>
//   );
// };

// export default ForgotPassword;
